import { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { User, LoginCredentials } from '../types';
import api from '../services/api';

const IDLE_TIMEOUT = 30 * 60 * 1000;
const IDLE_WARNING = 25 * 60 * 1000;

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('shipping_token');
    if (token) {
      api.setToken(token);
      loadUser();
    } else {
      setLoading(false);
    }
  }, []);

  const loadUser = async () => {
    try {
      const userData = await api.getProfile();
      setUser(userData);
    } catch (error) {
      localStorage.removeItem('shipping_token');
      api.setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials: LoginCredentials) => {
    const response = await api.login(credentials);
    const user = response.user;

    // Validate portal access - only employees with shipping portal access
    if (user.user_type !== 'employee') {
      throw new Error('This account does not have shipping portal access');
    }

    // Check allowed_portals array
    if (!user.allowed_portals?.includes('shipping')) {
      throw new Error('This account does not have shipping portal access');
    }

    // Use accessToken or access_token (support both)
    const token = response.access_token || (response as any).accessToken;
    localStorage.setItem('shipping_token', token);
    api.setToken(token);
    setUser(user);
  };

  const logout = useCallback(() => {
    localStorage.removeItem('shipping_token');
    api.setToken(null);
    setUser(null);
  }, []);

  const isAdmin = user?.all_access || user?.role === 'shipping_admin';

  const idleTimer = useRef<ReturnType<typeof setTimeout>>();
  const warningTimer = useRef<ReturnType<typeof setTimeout>>();
  const [showIdleWarning, setShowIdleWarning] = useState(false);

  useEffect(() => {
    if (!user) return;
    const resetIdle = () => {
      setShowIdleWarning(false);
      clearTimeout(idleTimer.current);
      clearTimeout(warningTimer.current);
      warningTimer.current = setTimeout(() => setShowIdleWarning(true), IDLE_WARNING);
      idleTimer.current = setTimeout(() => { logout(); window.location.href = '/login'; }, IDLE_TIMEOUT);
    };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    events.forEach(e => window.addEventListener(e, resetIdle, { passive: true }));
    resetIdle();
    return () => { events.forEach(e => window.removeEventListener(e, resetIdle)); clearTimeout(idleTimer.current); clearTimeout(warningTimer.current); };
  }, [user, logout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        loading,
        login,
        logout,
        isAdmin,
      }}
    >
      {children}
      {showIdleWarning && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, maxWidth: 360, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>&#9201;</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Session Expiring</h3>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>You'll be signed out in 5 minutes due to inactivity.</p>
            <button onClick={() => setShowIdleWarning(false)} style={{ padding: '8px 24px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Stay Signed In</button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
